import React, {useEffect, useState} from "react";
import {toast} from "react-toastify";
import {BETREIBER, FACHADMIN, SYSTEMADMIN, ZUST_SBD} from "../../../../consts/funktionaleRollen";
import {createInitialTeilentsorgungsraum} from "../../../../initialObjects/EntsorgungsraumObjects";
import {
    getEntsorgungsraumImportEndpoing,
    postTeilentsorgungsraumExport
} from "../../../../services/AnlagenService";
import {isApiCancel, useHttp} from "../../../../services/HttpService";
import {logger} from "../../../../services/LogService";
import {createColumn, createComponentColumn,} from "../../../../utils/ColumnUtils";
import {userHasAnyRole} from "../../../../utils/SecurityUtils";
import EAButtonBar from "../../../EAButtonBar";
import EAUploadButton from "../../../EAUploadButton";
import TeilentsorgungsraumDialog from "./TeilentsorgungsraumDialog";
import {storeIndexedValue} from "../../../../utils/IndexedArray";
import EATableActionButton from "../../../tableComponents/EATableActionButton";
import EATable from "../../../EATable";
import EADownloadButton from "../../../EADownloadButton";

function Entsorgungsraum({entsorgungsraeume, formFactory, onTeilentsorgungsraeumeUpdate, parentId, modus}) {
    const [teilentsorgungsraumDlgOpen, setTeilentsorgungsraumDlgOpen] = useState(false);
    const [volatileTeilentsorgungsraum, setVolatileTeilentsorgungsraum] = useState();
    const [disabled, setDisabled] = useState(true);
    const [{loading: importFileUploading}, uploadImportFile] = useHttp(
        {
            url: getEntsorgungsraumImportEndpoing(parentId),
            method: "POST",
        },
        {manual: true}
    );

    const id = "entsorgungsraum";

    useEffect(() => {
        if (!parentId) setDisabled(false);
        else if (modus === "s") {
            setDisabled(!userHasAnyRole([BETREIBER, FACHADMIN, SYSTEMADMIN, ZUST_SBD]));
        } else setDisabled(true);
    }, [parentId, modus]);

    function handleTeilentsorgungsraumDlgOpen(teilentsorgungsraum, index) {
        setVolatileTeilentsorgungsraum({...teilentsorgungsraum, index});
        setTeilentsorgungsraumDlgOpen(true);
    }

    function handleSaveTeilentsorgungsraum(newTeilentsorgungsraum) {
        newTeilentsorgungsraum.verwaltungseinheitSchluessel = newTeilentsorgungsraum.verwaltungseinheit.gemeindeNr;
        newTeilentsorgungsraum.verwaltungseinheitBezeichnung = newTeilentsorgungsraum.verwaltungseinheit.gemeindeBezeichnung;

        const newEntsorgungsraeume = [...entsorgungsraeume];
        storeIndexedValue(newEntsorgungsraeume, newTeilentsorgungsraum);
        setTeilentsorgungsraumDlgOpen(false);
        onTeilentsorgungsraeumeUpdate(newEntsorgungsraeume);
    }

    function handleTeilentsorgungsraumDelete(index) {
        const newEntsorgungsraeume = [...entsorgungsraeume];
        newEntsorgungsraeume.splice(index, 1);
        onTeilentsorgungsraeumeUpdate(newEntsorgungsraeume);
    }

    function onFileChange(e) {
        const importFile = e.target.files[0];
        if (importFile) {
            if (isXlsxFile(importFile)) {
                const formData = new FormData();
                formData.append("file", importFile, importFile.name);
                uploadImportFile({data: formData})
                    .then((response) => {
                        onTeilentsorgungsraeumeUpdate(response.data);
                        toast.success("Die Teilentsorgungsräume wurden erfolgreich importiert und können jetzt gespeichert werden.");
                    })
                    .catch((err) => {
                        if (isApiCancel(err)) {
                            return;
                        }
                        if (err.response) {
                            if (err.response.status === 400 && err.response.data?.message) {
                                toast.error(err.response.data.message);
                            } else {
                                logger.error(err.response);
                                toast.error("Der Import ist fehlgeschlagen!");
                            }
                        } else {
                            logger.error(err);
                            toast.error("Der Import ist fehlgeschlagen!");
                        }
                    });
            } else {
                logger.error("Import-Abbruch: Datei vom Typ " + importFile.type + " ausgewählt.");
                toast.error("Es können nur xlsx-Dateien importiert werden. Der Import wurde abgebrochen.");
            }
        }
    }

    function isXlsxFile(file) {
        return file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    }

    return (
        <div className="gridl tabsheet-content-margin" style={{gridTemplateColumns: "200px 200px 1fr"}}>
            <TeilentsorgungsraumDialog
                open={teilentsorgungsraumDlgOpen}
                onClose={() => setTeilentsorgungsraumDlgOpen(false)}
                teilentsorgungsraum={volatileTeilentsorgungsraum}
                onSaveTeilentsorgungsraum={handleSaveTeilentsorgungsraum}
            />
            <EATable
                id={id + "-teilentsorgungsraeume"}
                columns={getColumns(disabled, handleTeilentsorgungsraumDelete)}
                data={entsorgungsraeume}
                style={{gridColumn: "span 3"}}
            />
            {formFactory.renderError("entsorgungsraum.entsorgungsraeume", "entsorgungsraum.entsorgungsraeume-Error")}
            <EAButtonBar style={{gridColumn: "span 3", marginBottom: "var(--vertical-spacing)", marginLeft: "auto"}}>
                <EADownloadButton
                    id="export-entsorgungsraum"
                    label="Export"
                    url={postTeilentsorgungsraumExport(parentId)}
                    data={entsorgungsraeume}
                />
                {modus === "s" && (
                    <EAUploadButton
                        id={id + "-import"}
                        testId={id + "-import"}
                        label={"Import"}
                        accept={"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"}
                        onFileChange={onFileChange}
                        formFactory={formFactory}
                        disabled={importFileUploading}
                        loading={importFileUploading}
                    />
                )}
                {formFactory.renderButton(
                    id + "-teilentsorgungsraumHinzufuegen",
                    id + "-teilentsorgungsraumHinzufuegen",
                    "Hinzufügen",
                    () => {
                        handleTeilentsorgungsraumDlgOpen(createInitialTeilentsorgungsraum(), -1);
                    }
                )}
            </EAButtonBar>
        </div>
    );
}

function getColumns(disabled, onTeilentsorgungsraumDelete) {
    const columns = [];
    if (!disabled) {
        columns.push(
            createComponentColumn(
                "actions",
                (row, index) => {
                    return (
                        <div className="eatabwrapper">
                            <EATableActionButton id={"entsorgungsraumDeleteAction-" + index} iconName="ico-delete">
                                <span onClick={() => onTeilentsorgungsraumDelete(index)}/>
                            </EATableActionButton>
                        </div>
                    );
                },
                "",
                null,
                {width: 50}
            )
        );
    }
    return columns.concat([
        createColumn("verwaltungseinheitSchluessel", "Gemeinde-Nr.", {paddingLeft: "var(--horizontal-spacing)", width: "10em"}),
        createColumn("verwaltungseinheitBezeichnung", "Gemeinde")
    ]);
}

export default Entsorgungsraum;
